import { supabase } from '../lib/supabase';
import { StorageService } from './storageService';
import { 
  sanitizeAIResponse, 
  validatePlayerStats, 
  assessResultQuality,
  generateErrorMessage 
} from '../utils/screenshotValidation';

/**
 * Service for AI-powered screenshot processing
 * Handles image compression, API communication, and result validation
 */
export class AIProcessingService {
  
  /**
   * Process a match screenshot using AI
   * @param {File} screenshotFile - The screenshot file to process
   * @param {Object} matchContext - Context about the match
   * @returns {Promise<Object>} Processing result with player stats
   */
  static async processMatchScreenshot(screenshotFile, matchContext) {
    try {
      // Validate inputs
      if (!screenshotFile || !(screenshotFile instanceof File)) {
        throw new Error('Valid screenshot file is required');
      }

      if (!matchContext || !matchContext.team1Name || !matchContext.team2Name) {
        throw new Error('Match context with team names is required');
      }

      // Process screenshot for AI
      const processedImage = await StorageService.processScreenshotForAI(screenshotFile);
      
      // Prepare request payload
      const requestPayload = {
        image: processedImage.base64,
        matchContext: {
          team1Name: matchContext.team1Name,
          team2Name: matchContext.team2Name,
          expectedPlayers: matchContext.expectedPlayers || [],
          gameMode: matchContext.gameMode || '3v3'
        }
      };

      // Call Edge Function
      const { data, error } = await supabase.functions.invoke('process-match-screenshot', {
        body: requestPayload
      });

      if (error) {
        throw new Error(`API call failed: ${error.message}`);
      }

      // Sanitize the raw response
      const sanitizedData = sanitizeAIResponse(data);
      
      // Validate response structure
      const result = this.validateProcessingResult(sanitizedData);
      
      // Add quality assessment
      const qualityAssessment = assessResultQuality(result);
      
      // Add processing metadata
      result.metadata = {
        originalFileSize: processedImage.originalSize,
        compressedFileSize: processedImage.compressedSize,
        compressionRatio: processedImage.compressionRatio,
        processingTime: Date.now(),
        qualityAssessment
      };

      return result;

    } catch (error) {
      console.error('Error processing match screenshot:', error);
      
      // Generate user-friendly error message
      const errorInfo = generateErrorMessage('ai_processing_failed', { message: error.message });
      
      return {
        success: false,
        players: [],
        confidence: 0,
        error: errorInfo.message,
        metadata: {
          errorTime: Date.now(),
          originalError: error,
          errorInfo
        }
      };
    }
  }

  /**
   * Validate and normalize the processing result from the Edge Function
   * @param {Object} result - Raw result from Edge Function
   * @returns {Object} Validated and normalized result
   */
  static validateProcessingResult(result) {
    if (!result || typeof result !== 'object') {
      throw new Error('Invalid response from AI processing service');
    }

    // Check success flag
    if (!result.success) {
      return {
        success: false,
        players: [],
        confidence: 0,
        error: result.error || 'AI processing failed',
        debugInfo: result.debugInfo
      };
    }

    // Validate players array
    if (!Array.isArray(result.players)) {
      throw new Error('Invalid players data in AI response');
    }

    // Players are already normalized by sanitizeAIResponse
    const normalizedPlayers = result.players;

    // Validate confidence score
    const confidence = Math.min(1, Math.max(0, parseFloat(result.confidence) || 0));

    // Process extracted scores if available
    let extractedScores = null;
    if (result.extractedScores) {
      extractedScores = {
        team1Score: Math.max(0, parseInt(result.extractedScores.team1Score) || 0),
        team2Score: Math.max(0, parseInt(result.extractedScores.team2Score) || 0)
      };
    }

    // Extract MVP player if provided
    let mvpPlayer = null;
    if (result.mvpPlayer && typeof result.mvpPlayer === 'string') {
      mvpPlayer = result.mvpPlayer.trim();
      // Validate that the MVP player exists in the players list
      const mvpExists = normalizedPlayers.some(player => 
        player.playerName.toLowerCase() === mvpPlayer.toLowerCase()
      );
      if (!mvpExists) {
        console.warn(`MVP player "${mvpPlayer}" not found in players list, ignoring MVP`);
        mvpPlayer = null;
      }
    }

    // Validate player statistics
    const validation = validatePlayerStats(normalizedPlayers);
    
    return {
      success: true,
      players: normalizedPlayers,
      confidence,
      extractedScores,
      mvpPlayer,
      debugInfo: result.debugInfo,
      validation
    };
  }

  /**
   * Convert AI player data to match the MatchResultForm's expected format
   * @param {Array} aiPlayers - Players from AI processing (already sanitized with lowercase stats)
   * @param {Array} formPlayers - Existing players from the form (with team info)
   * @returns {Array} Merged player data ready for the form
   */
  static mergeAIDataWithFormPlayers(aiPlayers, formPlayers) {
    if (!Array.isArray(aiPlayers) || !Array.isArray(formPlayers)) {
      console.warn('Invalid data provided to mergeAIDataWithFormPlayers');
      return formPlayers;
    }

    // Debug logging for troubleshooting player matching issues
    console.log('🤖 AI Processing - Merging player data:');
    console.log('AI Players:', aiPlayers.map(p => ({ name: p.playerName, stats: p.stats })));
    console.log('Form Players:', formPlayers.map(p => ({ 
      name: p.player_name, 
      gamerTag: p.player_gamer_tag, 
      id: p.player_id 
    })));

    const mergedPlayers = formPlayers.map(formPlayer => {
      // Try to find matching AI player by name (fuzzy matching)
      const matchingAIPlayer = this.findMatchingPlayer(formPlayer, aiPlayers);
      
      if (matchingAIPlayer) {
        // AI response is already sanitized with lowercase keys
        const mergedPlayer = {
          ...formPlayer,
          goals: matchingAIPlayer.stats.goals || 0,
          assists: matchingAIPlayer.stats.assists || 0,
          passes: matchingAIPlayer.stats.passes || 0,
          interceptions: matchingAIPlayer.stats.interceptions || 0,
          saves: matchingAIPlayer.stats.saves || 0,
          // Keep existing form data for other fields
        };
        
        return mergedPlayer;
      }

      console.log(`❌ No match found for form player: ${formPlayer.player_name} (${formPlayer.player_gamer_tag || 'no gamer tag'})`);
      return formPlayer; // No AI data found, keep original
    });

    console.log('🔄 Merge completed. Players with AI stats:', mergedPlayers.filter(p => p.goals > 0 || p.assists > 0 || p.saves > 0).length);
    return mergedPlayers;
  }

  /**
   * Find matching player using fuzzy matching algorithms
   * @param {Object} formPlayer - Player from the form
   * @param {Array} aiPlayers - Players from AI
   * @returns {Object|null} Matching AI player or null
   */
  static findMatchingPlayer(formPlayer, aiPlayers) {
    const formName = this.normalizePlayerName(formPlayer.player_name);
    const formGamerTag = this.normalizePlayerName(formPlayer.player_gamer_tag || '');
    
    console.log(`   Normalized: ${formName} / ${formGamerTag}`);
    
    // Try exact name match first
    let match = aiPlayers.find(aiPlayer => {
      const aiNormalized = this.normalizePlayerName(aiPlayer.playerName);
      const isMatch = aiNormalized === formName;
      if (isMatch) {
      }
      return isMatch;
    });
    
    if (match) return match;

    // Try gamer tag match
    if (formGamerTag) {
      match = aiPlayers.find(aiPlayer => {
        const aiNormalized = this.normalizePlayerName(aiPlayer.playerName);
        const isMatch = aiNormalized === formGamerTag;
        if (isMatch) {
        }
        return isMatch;
      });
      if (match) return match;
    }

    // Try partial matches (contains)
    match = aiPlayers.find(aiPlayer => {
      const aiName = this.normalizePlayerName(aiPlayer.playerName);
      const isMatch = aiName.includes(formName) || formName.includes(aiName);
      if (isMatch) {
      }
      return isMatch;
    });
    
    if (match) return match;

    // Try similarity matching for typos
    match = aiPlayers.find(aiPlayer => {
      const aiName = this.normalizePlayerName(aiPlayer.playerName);
      const similarity = this.calculateSimilarity(formName, aiName);
      const isMatch = similarity > 0.8; // 80% similarity threshold
      if (isMatch) {
      }
      return isMatch;
    });

    if (match) return match;

    // Try similarity matching with gamer tag (if available)
    if (formGamerTag) {
      match = aiPlayers.find(aiPlayer => {
        const aiName = this.normalizePlayerName(aiPlayer.playerName);
        const similarity = this.calculateSimilarity(formGamerTag, aiName);
        const isMatch = similarity > 0.8;
        if (isMatch) {
        }
        return isMatch;
      });
    }

    if (!match) {
      console.log(`   ❌ No match found for: ${formPlayer.player_name} (${formPlayer.player_gamer_tag || 'no gamer tag'})`);
      console.log(`   Available AI players: ${aiPlayers.map(p => p.playerName).join(', ')}`);
    }

    return match || null;
  }

  /**
   * Normalize player name for comparison
   * @param {string} name - Player name to normalize
   * @returns {string} Normalized name
   */
  static normalizePlayerName(name) {
    if (!name) return '';
    return name.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, ''); // Remove special characters
  }

  /**
   * Calculate string similarity using Levenshtein distance
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} Similarity score between 0 and 1
   */
  static calculateSimilarity(str1, str2) {
    if (str1 === str2) return 1;
    if (str1.length === 0 || str2.length === 0) return 0;

    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    const maxLength = Math.max(str1.length, str2.length);
    return (maxLength - matrix[str2.length][str1.length]) / maxLength;
  }

  /**
   * Enhanced merge that handles unmatched AI players with intelligent fallback
   * @param {Array} aiPlayers - Players from AI processing
   * @param {Array} formPlayers - Existing players from the form
   * @returns {Object} Enhanced merge result with fallback assignments
   */
  static enhancedMergeWithFallback(aiPlayers, formPlayers) {
    if (!Array.isArray(aiPlayers) || !Array.isArray(formPlayers)) {
      console.warn('Invalid data provided to enhancedMergeWithFallback');
      return { mergedPlayers: formPlayers, unmatchedAI: [], suggestions: [] };
    }

    // First, try normal matching
    const normalMerged = this.mergeAIDataWithFormPlayers(aiPlayers, formPlayers);
    
    // Identify which AI players weren't matched
    const matchedAINames = new Set();
    normalMerged.forEach(formPlayer => {
      const matchingAI = this.findMatchingPlayer(formPlayer, aiPlayers);
      if (matchingAI) {
        matchedAINames.add(matchingAI.playerName);
      }
    });
    
    const unmatchedAI = aiPlayers.filter(aiPlayer => !matchedAINames.has(aiPlayer.playerName));
    
    // Create suggestions for unmatched AI players
    const suggestions = [];
    if (unmatchedAI.length > 0) {
      // Find form players with 0 stats (potential candidates for AI data)
      const candidateFormPlayers = formPlayers.filter(formPlayer => 
        formPlayer.goals === 0 && formPlayer.assists === 0 && formPlayer.saves === 0
      );
      
      unmatchedAI.forEach(aiPlayer => {
        // Find the best candidate form player for this AI player
        let bestCandidate = null;
        let bestScore = 0;
        
        candidateFormPlayers.forEach(candidate => {
          const nameScore = this.calculateSimilarity(
            this.normalizePlayerName(aiPlayer.playerName),
            this.normalizePlayerName(candidate.player_name)
          );
          const gamerTagScore = candidate.player_gamer_tag ? 
            this.calculateSimilarity(
              this.normalizePlayerName(aiPlayer.playerName),
              this.normalizePlayerName(candidate.player_gamer_tag)
            ) : 0;
          
          const finalScore = Math.max(nameScore, gamerTagScore);
          
          if (finalScore > bestScore && finalScore > 0.4) { // 40% threshold for suggestions
            bestScore = finalScore;
            bestCandidate = candidate;
          }
        });
        
        if (bestCandidate) {
          suggestions.push({
            aiPlayer,
            suggestedFormPlayer: bestCandidate,
            confidence: bestScore,
            reason: bestScore > 0.6 ? 'Good name similarity' : 'Possible match'
          });
        }
      });
    }
    
    console.log(`🔄 Enhanced merge completed:`, {
      totalAI: aiPlayers.length,
      matched: aiPlayers.length - unmatchedAI.length,
      unmatched: unmatchedAI.length,
      suggestions: suggestions.length
    });
    
    return {
      mergedPlayers: normalMerged,
      unmatchedAI,
      suggestions
    };
  }

  /**
   * Debug utility to analyze AI processing results and help troubleshoot matching issues
   * @param {Object} aiResult - Complete AI processing result
   * @param {Array} formPlayers - Form players for comparison
   * @returns {Object} Debug information
   */
  static debugAIProcessing(aiResult, formPlayers) {
    const debug = {
      timestamp: new Date().toISOString(),
      aiResult: {
        success: aiResult.success,
        confidence: aiResult.confidence,
        playersCount: aiResult.players?.length || 0,
        players: aiResult.players?.map(p => ({
          name: p.playerName,
          stats: p.stats,
          normalizedName: this.normalizePlayerName(p.playerName)
        })) || []
      },
      formPlayers: formPlayers.map(p => ({
        id: p.player_id,
        name: p.player_name,
        gamerTag: p.player_gamer_tag,
        normalizedName: this.normalizePlayerName(p.player_name),
        normalizedGamerTag: this.normalizePlayerName(p.player_gamer_tag || ''),
        currentStats: { goals: p.goals, assists: p.assists, saves: p.saves }
      })),
      matchingAnalysis: []
    };

    // Analyze each AI player against form players
    if (aiResult.players) {
      aiResult.players.forEach(aiPlayer => {
        const analysis = {
          aiPlayer: aiPlayer.playerName,
          matches: []
        };

        formPlayers.forEach(formPlayer => {
          const nameScore = this.calculateSimilarity(
            this.normalizePlayerName(aiPlayer.playerName),
            this.normalizePlayerName(formPlayer.player_name)
          );
          const gamerTagScore = formPlayer.player_gamer_tag ? 
            this.calculateSimilarity(
              this.normalizePlayerName(aiPlayer.playerName),
              this.normalizePlayerName(formPlayer.player_gamer_tag)
            ) : 0;

          analysis.matches.push({
            formPlayer: formPlayer.player_name,
            gamerTag: formPlayer.player_gamer_tag,
            nameScore: Math.round(nameScore * 100),
            gamerTagScore: Math.round(gamerTagScore * 100),
            bestScore: Math.round(Math.max(nameScore, gamerTagScore) * 100),
            wouldMatch: Math.max(nameScore, gamerTagScore) > 0.8
          });
        });

        // Sort matches by best score
        analysis.matches.sort((a, b) => b.bestScore - a.bestScore);
        debug.matchingAnalysis.push(analysis);
      });
    }

    return debug;
  }

  /**
   * Print debug information to console in a readable format
   * @param {Object} aiResult - AI processing result  
   * @param {Array} formPlayers - Form players
   */
  static printDebugInfo(aiResult, formPlayers) {
    const debug = this.debugAIProcessing(aiResult, formPlayers);
    
    console.group('🔍 AI Processing Debug Information');
    console.log('📊 AI Result:', {
      success: debug.aiResult.success,
      confidence: `${Math.round(debug.aiResult.confidence * 100)}%`,
      playersFound: debug.aiResult.playersCount
    });
    
    console.log('👥 AI Players Found:');
    debug.aiResult.players.forEach(player => {
      console.log(`  • ${player.name} (${player.normalizedName}) - Stats:`, player.stats);
    });
    
    console.log('📋 Form Players:');
    debug.formPlayers.forEach(player => {
      console.log(`  • ${player.name} (${player.gamerTag || 'no tag'}) - ID: ${player.id}`);
    });
    
    debug.matchingAnalysis.forEach(analysis => {
      console.log(`  AI Player: ${analysis.aiPlayer}`);
      const bestMatch = analysis.matches[0];
      if (bestMatch.wouldMatch) {
      } else {
        console.log(`    ❌ No good matches found. Best: ${bestMatch.formPlayer} (${bestMatch.bestScore}%)`);
      }
    });
    
    console.groupEnd();
    return debug;
  }

  /**
   * Validate extracted team scores against form data
   * @param {Object} extractedScores - Scores from AI
   * @param {Object} matchContext - Match context with team info
   * @returns {Object} Validation result
   */
  static validateExtractedScores(extractedScores, matchContext) {
    if (!extractedScores) {
      return {
        isValid: false,
        message: 'No scores were extracted from the screenshot'
      };
    }

    const { team1Score, team2Score } = extractedScores;
    
    // Basic validation
    if (team1Score < 0 || team2Score < 0) {
      return {
        isValid: false,
        message: 'Invalid negative scores detected'
      };
    }

    if (team1Score > 20 || team2Score > 20) {
      return {
        isValid: false,
        message: 'Unusually high scores detected - please verify'
      };
    }

    return {
      isValid: true,
      message: `Extracted scores: ${matchContext.team1Name} ${team1Score} - ${team2Score} ${matchContext.team2Name}`,
      suggestedScores: extractedScores
    };
  }

  /**
   * Generate processing summary for user display
   * @param {Object} result - Processing result
   * @returns {Object} Summary information
   */
  static generateProcessingSummary(result) {
    if (!result.success) {
      return {
        status: 'error',
        message: result.error || 'Processing failed',
        details: []
      };
    }

    const playerCount = result.players.length;
    const confidence = Math.round(result.confidence * 100);
    
    let status = 'success';
    let message = `Successfully extracted ${playerCount} player(s) with ${confidence}% confidence`;
    
    if (confidence < 70) {
      status = 'warning';
      message = `Extracted ${playerCount} player(s) but confidence is low (${confidence}%). Please review carefully.`;
    }

    const details = [
      `Players found: ${playerCount}`,
      `Confidence: ${confidence}%`,
      ...(result.extractedScores ? [`Team scores: ${result.extractedScores.team1Score} - ${result.extractedScores.team2Score}`] : []),
      ...(result.mvpPlayer ? [`MVP detected: ${result.mvpPlayer}`] : [])
    ];

    return {
      status,
      message,
      details,
      playerCount,
      confidence
    };
  }
}